// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CashflowImpactSectionContent } from '../../../types/proposal';
import { formatPlanCurrency } from './planFormat';

interface Props {
  readonly content: CashflowImpactSectionContent;
}

/** One line series of the frozen cashflow snapshot. */
interface CashflowSnapshotSeries {
  name: string;
  values: number[];
}

/**
 * Typed view over the snapshot the share flow freezes onto the section
 * content (`CashflowImpactSectionContent.snapshot` is an opaque record on the
 * builder-facing type — the snapshot only exists once shared).
 */
interface CashflowSnapshot {
  currency?: string;
  /** Calendar years for the X axis. */
  labels?: number[];
  series?: CashflowSnapshotSeries[];
}

/** Series colours — theme tokens read by recharts' SVG. */
const SERIES_COLORS = [
  'var(--color-purple-primary)',
  'var(--color-success-text)',
  'var(--color-error-text)',
  'var(--color-grey-03)',
];

const formatShort = (amount: number, currency?: string): string => {
  if (Math.abs(amount) >= 1000) {
    return `${(amount / 1000).toFixed(0)}k`;
  }
  return formatPlanCurrency(amount, currency);
};

/**
 * CASHFLOW_IMPACT — line chart built from the frozen snapshot
 * (`labels` = calendar years on X, one line per `series` entry) + the
 * advisor's caption and narrative. The snapshot only exists after share —
 * the chart is hidden gracefully when absent (e.g. a DRAFT opened via URL).
 */
const PlanCashflowSection: React.FC<Props> = ({ content }) => {
  const snapshot = (content.snapshot ?? undefined) as CashflowSnapshot | undefined;
  const labels = useMemo(() => snapshot?.labels ?? [], [snapshot]);
  const series = useMemo(() => snapshot?.series ?? [], [snapshot]);
  const currency = snapshot?.currency;

  const chartData = useMemo(
    () =>
      labels.map((year, i) => {
        const point: Record<string, number> = { year };
        for (const s of series) {
          point[s.name] = s.values[i];
        }
        return point;
      }),
    [labels, series],
  );

  return (
    <div data-testid="plan-cashflow">
      {chartData.length > 0 && series.length > 0 ? (
        <div className="proposal-plan__chart" data-testid="plan-cashflow-chart">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-grey-05)" />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => formatShort(Number(v), currency)}
              />
              <Tooltip
                formatter={(value) => formatPlanCurrency(Number(value), currency)}
                labelStyle={{ color: 'var(--color-black)' }}
              />
              <Legend />
              {series.map((s, i) => (
                <Line
                  key={s.name}
                  type="monotone"
                  dataKey={s.name}
                  name={s.name}
                  stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="proposal-plan__muted" data-testid="plan-cashflow-empty">
          No cashflow projection was included with this proposal.
        </p>
      )}

      {content.caption && (
        <p className="proposal-plan__caption" data-testid="plan-cashflow-caption">
          {content.caption}
        </p>
      )}
      {content.narrative && (
        <p className="proposal-plan__narrative" data-testid="plan-cashflow-narrative">
          {content.narrative}
        </p>
      )}
    </div>
  );
};

export default PlanCashflowSection;
